import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle,
  Target,
  WarningCircle,
  ChartBar,
  ArrowRight,
  ArrowDown,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

// ---------------- Market Profile education ----------------
const MP_TERMS = [
  {
    term: "POC (Point of Control)",
    desc: "Price level with the highest trading activity. Acts as a magnet for price.",
  },
  {
    term: "Value Area High (VAH)",
    desc: "Upper boundary of the 70% value area. Strong resistance when price is below.",
  },
  {
    term: "Value Area Low (VAL)",
    desc: "Lower boundary of the 70% value area. Strong support when price is above.",
  },
  {
    term: "Initial Balance (IB)",
    desc: "Range formed in the first 60 minutes. Sets the tone for the day.",
  },
  {
    term: "Single Prints / Poor Highs/Lows",
    desc: "Unfinished business — price often returns to fill these areas.",
  },
];

export function MarketProfileEducation() {
  return (
    <section
      data-testid="mp-education-section"
      className="px-5 lg:px-10 py-16 lg:py-24 bg-white"
    >
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-4">
            EDUCATION
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
            Market Profile —
            <br />
            Know Before You Trade
          </h2>
          <p className="font-body text-[#4A4A4A] mb-8 max-w-lg leading-relaxed">
            Our live sessions use Market Profile and Order Flow terminology.
            Download our free PDF guide covering the essential concepts so you
            are ready on Day 1.
          </p>

          <ul className="space-y-5">
            {MP_TERMS.map((t, i) => (
              <li
                key={i}
                className="flex items-start gap-3"
                data-testid={`mp-term-${i}`}
              >
                <CheckCircle
                  size={20}
                  weight="duotone"
                  className="text-[#3B5BDB] shrink-0 mt-0.5"
                />
                <p className="font-body text-[15px] text-[#0A0A0A] leading-relaxed">
                  <span className="font-bold">{t.term}:</span>{" "}
                  <span className="text-[#4A4A4A]">{t.desc}</span>
                </p>
              </li>
            ))}
          </ul>

          <Link
            to="/community/handbook"
            target="_blank"
            data-testid="view-pdf-btn"
            className="mt-10 inline-flex items-center gap-2 bg-[#3B5BDB] text-white px-6 py-3.5 font-body text-sm font-bold tracking-tight rounded-lg hover:bg-[#2B4BCC] transition-colors"
          >
            <BookOpen size={18} weight="bold" /> View Full PDF Guide
          </Link>
        </div>

        {/* Right: blue PDF card */}
        <div className="lg:col-span-5">
          <div className="bg-[#3B5BDB] text-white rounded-2xl p-6 lg:p-7 shadow-[0_18px_40px_-20px_rgba(59,91,219,0.45)]">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white rounded-lg w-12 h-12 flex items-center justify-center p-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_orderflow-preview/artifacts/727lksqr_photo_2025-11-28_14-12-44-removebg-preview.png"
                  alt="Great Eagle"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="font-display font-black text-lg leading-tight">
                  Great Eagle Tech
                </div>
                <div className="text-[#C7D0EE] text-xs">Market Profile Basics</div>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              {[
                { tag: "POC", color: "#E83D8C", label: "Highest volume node", w: "65%" },
                { tag: "VAH", color: "#3CCB7F", label: "Value Area High", w: "78%" },
                { tag: "VAL", color: "#F76E5F", label: "Value Area Low", w: "45%" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center justify-center font-mono-acc font-bold text-xs px-2.5 py-1.5 rounded-md text-white shrink-0 min-w-[42px]"
                    style={{ backgroundColor: b.color }}
                  >
                    {b.tag}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="bg-[#5C7AE5] rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ backgroundColor: b.color, width: b.w }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-[#C7D0EE] hidden sm:inline shrink-0">
                    {b.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-white/15">
              <p className="text-xs text-[#C7D0EE]">
                Full PDF with all terminologies available to all members.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------- How to Get Started ----------------
const STARTED_STEPS = [
  {
    n: "01",
    title: "Register & Pay",
    body: "Fill the short form and pay Rs. 999 via Razorpay — secure & instant.",
  },
  {
    n: "02",
    title: "Get WhatsApp Link",
    body: "The WhatsApp group link is revealed immediately after your payment is confirmed.",
  },
  {
    n: "03",
    title: "Join Live Sessions",
    body: "Join the group, receive your Zoom link, and start trading with us!",
  },
];

export function HowToGetStarted({ onRegisterClick }) {
  return (
    <section
      data-testid="how-to-section"
      id="how-to-get-started"
      className="bg-[#2A3DA8] text-white px-5 lg:px-10 py-16 lg:py-24"
    >
      <div className="max-w-[1100px] mx-auto text-center">
        <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter leading-tight mb-3">
          How to Get Started
        </h2>
        <p className="text-[#C7D0EE] font-body text-base mb-12">
          Three simple steps to start trading live with us
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {STARTED_STEPS.map((s) => (
            <div
              key={s.n}
              data-testid={`started-step-${s.n}`}
              className="bg-[#3B5BDB] rounded-2xl p-7 text-left"
            >
              <div className="font-display font-black text-5xl text-[#FFD43B] leading-none mb-5">
                {s.n}
              </div>
              <h3 className="font-display font-black text-xl tracking-tight mb-3">
                {s.title}
              </h3>
              <p className="text-[#C7D0EE] text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <button
          data-testid="started-register-btn"
          onClick={onRegisterClick}
          className="inline-flex items-center gap-3 bg-[#E83D5E] hover:bg-[#C72D4A] text-white px-8 py-4 rounded-lg font-body text-base font-bold tracking-tight transition-colors shadow-[0_18px_40px_-15px_rgba(232,61,94,0.6)]"
        >
          Register & Pay Rs. 999 <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </section>
  );
}

// ---------------- Instructions for New Members ----------------
const INSTRUCTION_GROUPS = [
  {
    icon: CheckCircle,
    color: "#3CCB7F",
    title: "Before Your First Session",
    items: [
      "Read the Market Profile terminology PDF",
      "Create a new email ID for the free charting platform trial",
      "Download Zoom on your device",
      "Join the WhatsApp group — link revealed after payment",
    ],
  },
  {
    icon: WarningCircle,
    color: "#F59F00",
    title: "Community Guidelines",
    items: [
      "Be respectful and civil at all times",
      "No spamming or irrelevant messages in the group",
      "Do not share session recording links externally",
      "Questions during live: use the chat / raise hand feature",
    ],
  },
  {
    icon: ChartBar,
    color: "#3B5BDB",
    title: "Understanding the Levels",
    items: [
      "R1 / R2 / R3 are resistance levels above POC",
      "S1 / S2 / S3 are support levels below POC",
      "BULLISH level: bias turns positive above this",
      "BEARISH level: bias turns negative below this",
    ],
  },
  {
    icon: Target,
    color: "#E83D5E",
    title: "During Live Sessions",
    items: [
      "Supply / Demand zones will be updated live",
      "Always use proper risk management",
      "Do not take trades based on levels alone — context matters",
      "Inner range targets shared exclusively in group",
    ],
  },
];

export function InstructionsForNewMembers() {
  return (
    <section
      data-testid="instructions-section"
      className="bg-white px-5 lg:px-10 py-16 lg:py-24"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-12">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
            NEW JOINEE GUIDE
          </div>
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter">
            Instructions for New Members
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {INSTRUCTION_GROUPS.map((g, i) => {
            const Icon = g.icon;
            return (
              <div
                key={i}
                data-testid={`instr-group-${i}`}
                className="border border-[#E5E5E0] bg-[#FAFAF7] rounded-xl p-7"
              >
                <div className="flex items-center gap-2.5 mb-5">
                  <Icon
                    size={22}
                    weight="duotone"
                    style={{ color: g.color }}
                  />
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {g.title}
                  </h3>
                </div>
                <ul className="space-y-2.5">
                  {g.items.map((item, k) => (
                    <li
                      key={k}
                      className="flex gap-3 text-[14px] text-[#0A0A0A] leading-relaxed"
                    >
                      <span className="text-[#3B5BDB] mt-2 shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/community/handbook"
            target="_blank"
            data-testid="view-instructions-sheet"
            className="inline-flex items-center gap-2 border border-[#3B5BDB] text-[#3B5BDB] px-6 py-3 rounded-lg font-body text-sm font-bold tracking-tight hover:bg-[#3B5BDB] hover:text-white transition-colors"
          >
            <BookOpen size={16} weight="bold" /> View Full Instructions Sheet
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------- Daily levels sample ----------------
const SAMPLE_LEVELS = [
  { label: "R3", nifty: 23970, bnf: 55230, mid: 14500 },
  { label: "R2", nifty: 23900, bnf: 55000, mid: 14448 },
  { label: "R1", nifty: 23857, bnf: 54879, mid: 14400 },
  { label: "BULLISH", nifty: 23690, bnf: 54600, mid: 14350, bg: "#C9E4C9" },
  { label: "POC", nifty: 23690, bnf: 54349, mid: 14152, bg: "#F08CD1" },
  { label: "BEARISH", nifty: 23540, bnf: 53784, mid: 14080, bg: "#D9938F" },
  { label: "S1", nifty: 23490, bnf: 53585, mid: 14040 },
  { label: "S2", nifty: 23440, bnf: 53400, mid: 13990 },
  { label: "S3", nifty: 23385, bnf: 53185, mid: 13935 },
];

export function DailyLevelsSample() {
  return (
    <section
      data-testid="levels-section"
      className="bg-[#FAFAF7] px-5 lg:px-10 py-16 lg:py-24"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-10">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
            DAILY LEVELS · SHARED EVERY MORNING
          </div>
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter">
            This is what you get
            <br />
            before market open.
          </h2>
          <p className="font-body text-[#4A4A4A] mt-4 max-w-xl mx-auto">
            A clean levels chart for Nifty, Bank Nifty and Midcap — every
            single trading day. Inner range targets are shared live in the
            group.
          </p>
        </div>

        <div className="max-w-[760px] mx-auto border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="levels-table">
            <thead className="bg-[#E8EEFB]">
              <tr>
                <th
                  colSpan={4}
                  className="text-left px-4 py-3 border-b-2 border-[#0A0A0A]"
                >
                  <div className="flex items-center gap-6 font-display font-black text-xl tracking-tight">
                    <span>Level Trades</span>
                    <span className="font-mono-acc text-base text-[#4A4A4A]">
                      15 May 2026
                    </span>
                  </div>
                </th>
              </tr>
              <tr className="bg-[#E8EEFB] border-b border-[#0A0A0A]">
                <th className="text-left px-4 py-2.5 font-display font-bold text-base">
                  Level
                </th>
                <th className="text-right px-4 py-2.5 font-display font-bold text-base">
                  NIFTY
                </th>
                <th className="text-right px-4 py-2.5 font-display font-bold text-base">
                  BNIFTY
                </th>
                <th className="text-right px-4 py-2.5 font-display font-bold text-base">
                  MIDCAP
                </th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_LEVELS.map((r, i) => (
                <tr
                  key={r.label}
                  className="border-b border-[#D4D0C5]"
                  style={r.bg ? { backgroundColor: r.bg } : {}}
                  data-testid={`level-row-${r.label}`}
                >
                  <td className="px-4 py-2.5 font-display font-bold">
                    {r.label}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-acc tabular-nums">
                    {r.nifty.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-acc tabular-nums">
                    {r.bnf.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-acc tabular-nums">
                    {r.mid.toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-[#4A4A4A] mt-5 font-mono-acc tracking-[0.15em]">
          Sample · Actual levels are shared in the WhatsApp group every market day at ~09:00 IST.
        </p>
      </div>
    </section>
  );
}

// ---------------- FAQ ----------------

const FAQS = [
  {
    q: "What do I get for Rs. 999?",
    a: "10 trading days of live support in our WhatsApp trading group — that works out to just ₹99 per trading day. Every market day you'll get the daily trade plan, Nifty / Bank Nifty / Midcap futures levels, range updates through the day, and access to our EOD discussion sessions live. It's a low-cost way to get introduced to our trading ecosystem.",
  },
  {
    q: "How do I join after payment?",
    a: "Make the payment via the Razorpay button on this page and enter your unique Razorpay Payment ID. Once we verify it, the WhatsApp group link is revealed instantly — join and you'll start receiving the trade plan the next market open.",
  },
  {
    q: "What markets do you cover?",
    a: "Indian index futures — Nifty 50, Bank Nifty, Nifty Midcap. Course members get additional MCX coverage on our course-only group.",
  },
  {
    q: "Do I need prior trading knowledge?",
    a: "No. Beginners are welcome. But please set the right expectation — we don't do full-day market commentary. We follow our trade plan strictly, so trades are few. Watch, learn the process, and only take positions once you understand our style.",
  },
  {
    q: "What happens after 10 trading days?",
    a: "You can renew for another 10 trading days at the same price, or upgrade to our structured 3 / 6 / 12 month mentorship packages (Starter · Core · Pro) — community members get priority onboarding.",
  },
];

export function CommunityFAQ() {
  return (
    <section
      data-testid="community-faq-section"
      className="bg-[#FAFAF7] px-5 lg:px-10 py-16 lg:py-24 border-t border-[#E5E5E0]"
    >
      <div className="max-w-[820px] mx-auto">
        <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-center mb-10">
          Frequently Asked Questions
        </h2>

        <Accordion type="single" collapsible className="space-y-4" data-testid="community-faq">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`q-${i}`}
              className="bg-white rounded-xl border border-[#E5E5E0] px-5 [&[data-state=open]]:shadow-[0_8px_24px_-12px_rgba(10,10,10,0.12)]"
              data-testid={`community-faq-${i}`}
            >
              <AccordionTrigger className="hover:no-underline py-5 font-display font-bold text-base lg:text-lg tracking-tight text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="font-body text-[#4A4A4A] text-sm lg:text-base leading-relaxed pb-5 pr-2">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ---------------- Footer ----------------
export function CommunityFooter() {
  return (
    <footer
      data-testid="community-footer"
      className="bg-[#0A1D33] text-[#A0B3CC] px-5 lg:px-10 py-7"
    >
      <div className="max-w-[1100px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-md w-9 h-9 flex items-center justify-center p-1">
            <img
              src="https://customer-assets.emergentagent.com/job_orderflow-preview/artifacts/727lksqr_photo_2025-11-28_14-12-44-removebg-preview.png"
              alt="Great Eagle"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="text-white font-display font-bold text-sm">
              Great Eagle Tech
            </div>
            <div className="text-xs text-[#6A82A0]">Trading Education &amp; Community</div>
          </div>
        </div>

        <div className="flex items-center gap-6 text-xs">
          <span className="inline-flex items-center gap-2">
            ☎ Pay Rs. 999
          </span>
          <span className="inline-flex items-center gap-2">
            🔒 WhatsApp Group (members only)
          </span>
        </div>

        <div className="text-xs text-[#6A82A0]">
          © {new Date().getFullYear()} Great Eagle Tech. Trading involves risk.
        </div>
      </div>
    </footer>
  );
}

// "Scroll to top of form" anchor helper
export function ScrollDownHint() {
  return (
    <div className="text-center -mt-2 mb-4">
      <ArrowDown size={20} className="inline-block animate-bounce text-[#D9381E]" />
    </div>
  );
}
